import { create } from 'zustand';
import { supabase } from '../supabase';

async function getOrgId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase.from('user_profiles').select('organization_id').eq('id', user.id).single();
  return data?.organization_id || null;
}

// --- Types ---

export type DdtTemplateDirection = 'inbound' | 'outbound' | 'both';

// Discriminatore di struttura HTML: 'classic' = layout gestionale AGORA
// (header logo+dati/box destinatario, griglia totali con rowspan);
// 'boxed_totals' = layout fattura accompagnatoria a blocchi (box totali
// impilati a destra), es. Innovaliving srl; 'courier_cod' = layout
// contrassegno/corriere con box IBAN, es. India World Srl;
// 'order_confirmation' = layout conferma d'ordine fornitore con foto
// prodotto/sconti a scaglioni/pagina Avvertenze, es. Andrea Bizzotto SPA;
// 'client_order' = layout ordine cliente con header/fascia doc ripetuti su
// ogni pagina (Pagina X di Y) e box Totale ordine solo sull'ultima, es.
// Magazzini Cosma; 'packing_list' = tabulato dati landscape (colli/pesi/
// volumi/dogana), intestazione colonne ripetuta nativamente via <thead>,
// nessun footer, es. ITEM International; 'transport_multilang' = DDT
// D.P.R. 472/1996 con box Cliente/Luogo di consegna affiancati, tabella a 4
// colonne senza prezzi (nota legale sotto ogni riga prodotto) e griglia
// trasporto/firme, etichette tradotte in base a `language`, es. Say Farm;
// 'boxed_transport' = DDT a riquadri con intestazioni su fondo grigio, lotto e
// scadenza sotto la descrizione e piede colli/peso/firme ripetuto su ogni
// pagina, es. Ideariso.
// Vedi printDdtWithTemplate in ClassicDdtPrint.ts per il dispatch.
export type DdtTemplateLayout = 'classic' | 'boxed_totals' | 'courier_cod' | 'order_confirmation' | 'client_order' | 'packing_list' | 'transport_multilang' | 'boxed_transport';

// Lingua delle etichette stampate — usata solo dal layout 'transport_multilang'
// (gli altri layout hanno etichette italiane fisse). Una riga template per
// lingua: l'utente sceglie la variante dal modale di stampa già esistente.
export type DdtTemplateLanguage = 'it' | 'en' | 'fr';

export interface DdtPrintTemplate {
  id: string;
  organization_id: string;
  brand_id: string | null;
  name: string;
  layout: DdtTemplateLayout;
  language: DdtTemplateLanguage;
  logo_url: string | null;
  company_name: string | null;
  address_line1: string | null;
  address_line2: string | null;
  phone: string | null;
  fax: string | null;
  email: string | null;
  website: string | null;
  vat_number: string | null;
  tax_code: string | null;
  rea_cciaa: string | null;
  share_capital: string | null;
  // Coordinate bancarie del fornitore/mittente — usate solo dal layout 'courier_cod'
  iban: string | null;
  bic: string | null;
  // Codice destinatario SDI stampato in intestazione — layout 'transport_multilang'
  sdi_code: string | null;
  // Coordinate bancarie in chiaro, una riga per banca (IBAN + nome banca):
  // il layout 'transport_multilang' le stampa nel blocco intestazione così
  // come sono, senza etichette
  bank_details: string | null;
  // Nota stampata sotto la descrizione di ogni riga collegata a un prodotto
  // (es. dicitura di legge sulla canapa sativa). Le righe senza prodotto
  // (rimborsi trasporto/assicurazione) restano pulite; il campo `detail`
  // della singola riga, se compilato, ha la precedenza.
  line_default_note: string | null;
  // Righe libere stampate sotto l'indirizzo in intestazione (es. indirizzo del
  // magazzino, nazione): una riga vuota separa i blocchi — layout 'boxed_transport'
  header_extra: string | null;
  // Chiavi dei campi extra abilitati per questo template (vedi
  // src/lib/ddtExtraFields.ts) — determina quali campi extra mostrare nei
  // form Carico Merce/Spedizione quando questo template è selezionato come
  // "Template di riferimento" sul DDT. 100% personalizzabile, non limitato
  // ai campi "suggeriti" dal layout del template.
  enabled_extra_fields: string[] | null;
  sale_conditions: string | null;
  annotations: string | null;
  show_prices: boolean;
  show_vat: boolean;
  direction: DdtTemplateDirection;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// --- Store ---

interface DdtPrintTemplateStore {
  templates: DdtPrintTemplate[];
  loading: boolean;
  loaded: boolean;
  error: string | null;
  fetchTemplates: () => Promise<void>;
  getActiveTemplates: (direction: 'inbound' | 'outbound') => DdtPrintTemplate[];
  createTemplate: (data: Partial<Omit<DdtPrintTemplate, 'id' | 'organization_id' | 'created_at' | 'updated_at'>> & { name: string }) => Promise<DdtPrintTemplate | null>;
  updateTemplate: (id: string, data: Partial<DdtPrintTemplate>) => Promise<boolean>;
  deleteTemplate: (id: string) => Promise<boolean>;
  clearError: () => void;
}

export const useDdtPrintTemplateStore = create<DdtPrintTemplateStore>((set, get) => ({
  templates: [],
  loading: false,
  loaded: false,
  error: null,
  clearError: () => set({ error: null }),

  fetchTemplates: async () => {
    set({ loading: true, error: null });
    try {
      const orgId = await getOrgId();
      if (!orgId) throw new Error('Organizzazione non trovata');
      const { data, error } = await supabase
        .from('wh_ddt_print_templates')
        .select('*')
        .eq('organization_id', orgId)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      set({ templates: (data || []) as DdtPrintTemplate[], loading: false, loaded: true });
    } catch (err: any) {
      set({ error: err.message, loading: false, loaded: true });
    }
  },

  getActiveTemplates: (direction) => {
    return get().templates.filter(t => t.is_active && (t.direction === 'both' || t.direction === direction));
  },

  createTemplate: async (formData) => {
    try {
      const orgId = await getOrgId();
      if (!orgId) return null;
      const { data, error } = await supabase
        .from('wh_ddt_print_templates')
        .insert({ ...formData, organization_id: orgId })
        .select()
        .single();
      if (error) throw error;
      set(state => ({ templates: [...state.templates, data as DdtPrintTemplate] }));
      return data as DdtPrintTemplate;
    } catch (err: any) {
      set({ error: err.message });
      return null;
    }
  },

  updateTemplate: async (id, data) => {
    try {
      const { error } = await supabase.from('wh_ddt_print_templates').update(data).eq('id', id);
      if (error) throw error;
      set(state => ({ templates: state.templates.map(t => t.id === id ? { ...t, ...data } : t) }));
      return true;
    } catch { return false; }
  },

  deleteTemplate: async (id) => {
    try {
      const { error } = await supabase.from('wh_ddt_print_templates').delete().eq('id', id);
      if (error) throw error;
      set(state => ({ templates: state.templates.filter(t => t.id !== id) }));
      return true;
    } catch { return false; }
  },
}));
